import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import {
  AuditSchedule,
  ListAuditSchedulesRequest,
  CreateAuditScheduleRequest,
  UpdateAuditScheduleRequest,
  GetAuditScheduleRequest,
} from 'src/types/helios/AuditSchedule';
import { scheduleService } from 'src/data/api/helios-service/scheduleService';
import { handleError } from 'src/utils/generalUtils';
interface SchedulesState {
  schedulesList: AuditSchedule[];
  currentSchedule?: AuditSchedule;
  getSchedulesApiStatus: GetSchedulesApiStatus;
  fetchSchedulesApiStatus: ApiRequestStatus;
  createScheduleApiStatus: ApiRequestStatus;
  updateScheduleApiStatus: ApiRequestStatus;
  deleteScheduleApiStatus: ApiRequestStatus;
}

export interface GetSchedulesApiStatus extends ApiRequestStatus {
  auditSchedule?: AuditSchedule;
}
const initialState: SchedulesState = {
  schedulesList: [],
  fetchSchedulesApiStatus: initApiStatus,
  createScheduleApiStatus: initApiStatus,
  updateScheduleApiStatus: initApiStatus,
  deleteScheduleApiStatus: initApiStatus,
  getSchedulesApiStatus: initApiStatus,
};

export const loadSchedulesList = createAsyncThunk(
  'schedules/loadSchedulesList',
  async (params: ListAuditSchedulesRequest, { rejectWithValue }) => {
    try {
      const response = await scheduleService.getAll(params);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const createSchedule = createAsyncThunk(
  'schedules/createSchedule',
  async (params: CreateAuditScheduleRequest, { rejectWithValue }) => {
    try {
      const response = await scheduleService.create(params);
      return {
        schedule: response.schedule,
        message: 'Schedule created successfully',
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const getScheduleById = createAsyncThunk(
  'schedules/getScheduleById',
  async (data: GetAuditScheduleRequest, { rejectWithValue }) => {
    try {
      const response = await scheduleService.getById(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const updateSchedule = createAsyncThunk(
  'schedules/updateSchedule',
  async (params: UpdateAuditScheduleRequest, { rejectWithValue }) => {
    try {
      const response = await scheduleService.update(params);
      return {
        schedule: response.schedule,
        message: 'Schedule updated successfully',
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const deleteSchedule = createAsyncThunk(
  'schedules/deleteSchedule',
  async (
    { programId, scheduleId }: { programId: string; scheduleId: string },
    { rejectWithValue },
  ) => {
    try {
      await scheduleService.delete(programId, scheduleId);
      return {
        scheduleId,
        message: 'Schedule deleted successfully',
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

const schedulesSlice = createSlice({
  name: 'schedules',
  initialState,
  reducers: {
    resetCreateScheduleApiStatus: (state) => {
      state.createScheduleApiStatus = initialState.createScheduleApiStatus;
    },
    resetUpdateScheduleApiStatus: (state) => {
      state.updateScheduleApiStatus = initialState.updateScheduleApiStatus;
    },
    resetDeleteScheduleApiStatus: (state) => {
      state.deleteScheduleApiStatus = initialState.deleteScheduleApiStatus;
    },
    resetAllApiStatus: (state) => {
      state.fetchSchedulesApiStatus = initialState.fetchSchedulesApiStatus;
      state.createScheduleApiStatus = initialState.createScheduleApiStatus;
      state.updateScheduleApiStatus = initialState.updateScheduleApiStatus;
      state.deleteScheduleApiStatus = initialState.deleteScheduleApiStatus;
      state.getSchedulesApiStatus = initialState.deleteScheduleApiStatus;
    },
    addScheduleToState: (state, action: PayloadAction<AuditSchedule>) => {
      state.schedulesList.push(action.payload);
    },
    updateScheduleInState: (state, action: PayloadAction<AuditSchedule>) => {
      const index = state.schedulesList.findIndex(
        (schedule) => schedule.scheduleId === action.payload.scheduleId,
      );
      if (index !== -1) {
        state.schedulesList[index] = action.payload;
      }
    },
    clearAPIStatusMessages: (state) => {
      state.createScheduleApiStatus.errorMessage = '';
      state.createScheduleApiStatus.successMessage = '';
      state.updateScheduleApiStatus.errorMessage = '';
      state.updateScheduleApiStatus.successMessage = '';
      state.deleteScheduleApiStatus.errorMessage = '';
      state.deleteScheduleApiStatus.successMessage = '';
    },
  },
  extraReducers: (builder) => {
    builder
      // Load schedules list
      .addCase(loadSchedulesList.pending, (state) => {
        state.fetchSchedulesApiStatus.isLoading = true;
        state.fetchSchedulesApiStatus.isSuccess = false;
        state.fetchSchedulesApiStatus.errorMessage = null;
        state.schedulesList = [];
      })
      .addCase(loadSchedulesList.fulfilled, (state, action) => {
        state.fetchSchedulesApiStatus.isLoading = false;
        state.fetchSchedulesApiStatus.isSuccess = true;
        if (action.meta.arg.templateId) {
          state.schedulesList = action.payload.schedules.filter((schedule) =>
            schedule.auditTemplateIds.includes(action.meta.arg.templateId!),
          );
        } else {
          state.schedulesList = action.payload.schedules;
        }
      })
      .addCase(loadSchedulesList.rejected, (state, action) => {
        state.fetchSchedulesApiStatus.isLoading = false;
        state.fetchSchedulesApiStatus.isSuccess = true;
        state.schedulesList = [];
        state.fetchSchedulesApiStatus.errorMessage = 'Rejected';
      })
      // Get schedules by id
      .addCase(getScheduleById.pending, (state) => {
        state.getSchedulesApiStatus.isLoading = true;
        state.getSchedulesApiStatus.isSuccess = false;
        state.getSchedulesApiStatus.errorMessage = null;
      })
      .addCase(getScheduleById.fulfilled, (state, action) => {
        state.getSchedulesApiStatus.isLoading = false;
        state.getSchedulesApiStatus.isSuccess = true;
        state.getSchedulesApiStatus.auditSchedule = action.payload.schedule;
      })
      .addCase(getScheduleById.rejected, (state, action) => {
        state.fetchSchedulesApiStatus.isLoading = false;
        state.fetchSchedulesApiStatus.isSuccess = false;
        state.fetchSchedulesApiStatus.errorMessage = action.payload as string;
      });

    // Create schedule
    builder.addCase(createSchedule.pending, (state) => {
      state.createScheduleApiStatus.isLoading = true;
      state.createScheduleApiStatus.isSuccess = false;
      state.createScheduleApiStatus.errorMessage = '';
    });
    builder.addCase(createSchedule.fulfilled, (state, action) => {
      state.createScheduleApiStatus.isLoading = false;
      state.createScheduleApiStatus.isSuccess = true;

      schedulesSlice.caseReducers.addScheduleToState(state, {
        type: 'addScheduleToState',
        payload: action.payload.schedule,
      });
    });
    builder.addCase(createSchedule.rejected, (state, action) => {
      state.createScheduleApiStatus.isLoading = false;
      state.createScheduleApiStatus.errorMessage = action.payload as string;
    });

    // Update schedule
    builder.addCase(updateSchedule.pending, (state) => {
      state.updateScheduleApiStatus.isLoading = true;
      state.updateScheduleApiStatus.isSuccess = false;
      state.updateScheduleApiStatus.errorMessage = '';
    });
    builder.addCase(updateSchedule.fulfilled, (state, action) => {
      state.updateScheduleApiStatus.isLoading = false;
      state.updateScheduleApiStatus.isSuccess = true;
      schedulesSlice.caseReducers.updateScheduleInState(state, {
        type: 'updateScheduleInState',
        payload: action.payload.schedule,
      });
    });
    builder.addCase(updateSchedule.rejected, (state, action) => {
      state.updateScheduleApiStatus.isLoading = false;
      state.updateScheduleApiStatus.errorMessage = action.payload as string;
    });

    // Delete schedule
    builder.addCase(deleteSchedule.pending, (state) => {
      state.deleteScheduleApiStatus.isLoading = true;
      state.deleteScheduleApiStatus.isSuccess = false;
      state.deleteScheduleApiStatus.errorMessage = '';
    });
    builder.addCase(deleteSchedule.fulfilled, (state, action) => {
      state.deleteScheduleApiStatus.isLoading = false;
      state.deleteScheduleApiStatus.isSuccess = true;
    });
    builder.addCase(deleteSchedule.rejected, (state, action) => {
      state.deleteScheduleApiStatus.isLoading = false;
      state.deleteScheduleApiStatus.isSuccess = false;
      state.deleteScheduleApiStatus.errorMessage = action.payload as string;
    });
  },
});

export const {
  resetCreateScheduleApiStatus,
  resetUpdateScheduleApiStatus,
  resetDeleteScheduleApiStatus,
  resetAllApiStatus,
} = schedulesSlice.actions;

export default schedulesSlice.reducer;
